/**
 * ProgressDots - Shared Progress Indicator Component
 *
 * Renders progress dots based on CANONICAL stage from jobboss.job_stages.
 *
 * CRITICAL DESIGN DECISIONS:
 * - Stage status is derived from the `stage` prop (canonical stage from job_stages)
 * - NOT inferred from milestones - milestones are for tooltip detail only
 * - Uses canonical 5-stage ordering: PENDING -> PROD_ART -> PRINTING -> ASSEMBLY -> SHIPPING
 *
 * Usage:
 *   <ProgressDots stage="PRINTING" />
 *   <ProgressDots stage={job.stage} milestones={job.milestones} compact />
 *
 * @see IMPLEMENTATION-PLAN.md - Job Progress Contract
 */

import React from 'react';

// CANONICAL STAGE ORDERING - includes PENDING (5 stages)
// Matches backend: stageCalculationService.js STAGE_ORDER
export const CANONICAL_STAGES = ['PENDING', 'PROD_ART', 'PRINTING', 'ASSEMBLY', 'SHIPPING'];

// Display labels for UI
export const STAGE_LABELS = {
  PENDING: 'Pending',
  PROD_ART: 'Art',
  PRINTING: 'Print',
  ASSEMBLY: 'Assembly',
  SHIPPING: 'Ship',
};

// Short labels for very compact displays
export const STAGE_SHORT_LABELS = {
  PENDING: 'P',
  PROD_ART: 'A',
  PRINTING: 'PR',
  ASSEMBLY: 'AS',
  SHIPPING: 'SH',
};

/**
 * Renders progress dots based on CANONICAL stage from jobboss.job_stages
 *
 * @param {string} stage - The canonical stage from job_stages.stage (REQUIRED)
 * @param {object} milestones - Optional, for tooltip detail only (NOT for status derivation)
 * @param {boolean} compact - Smaller dots for inline display (default: false)
 * @param {boolean} showPending - Whether to show PENDING dot (default: false for most views)
 * @param {boolean} showLabels - Show text labels under dots (default: false)
 * @param {string} size - Size preset: 'sm' | 'md' | 'lg' (default: 'md')
 */
export function ProgressDots({
  stage,
  milestones = {},
  compact = false,
  showPending = false,
  showLabels = false,
  size = 'md',
}) {
  // Filter stages based on showPending option
  const displayStages = showPending
    ? CANONICAL_STAGES
    : CANONICAL_STAGES.filter((s) => s !== 'PENDING');

  // Size classes
  const sizeClasses = {
    sm: 'w-1.5 h-1.5',
    md: compact ? 'w-2 h-2' : 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  const gapClasses = {
    sm: 'gap-0.5',
    md: compact ? 'gap-1' : 'gap-2',
    lg: 'gap-2',
  };

  const dotSize = sizeClasses[size] || sizeClasses.md;
  const gapSize = gapClasses[size] || gapClasses.md;

  // Derive status purely from canonical stage position
  const getStageStatus = (displayStage) => {
    const stageIndex = CANONICAL_STAGES.indexOf(displayStage);
    const currentIndex = CANONICAL_STAGES.indexOf(stage);

    if (currentIndex === -1) return 'unknown'; // Invalid stage
    if (stageIndex < currentIndex) return 'complete';
    if (stageIndex === currentIndex) return 'current';
    return 'pending';
  };

  // Build tooltip from stage status (milestones for future detail)
  const getTooltip = (displayStage) => {
    const status = getStageStatus(displayStage);
    const label = STAGE_LABELS[displayStage];
    return `${label}: ${status}`;
  };

  // Status to color mapping
  const statusColors = {
    complete: 'bg-green-500',
    current: 'bg-blue-500',
    pending: 'bg-gray-300',
    unknown: 'bg-red-300',
  };

  return (
    <div className={`flex items-center ${gapSize}`}>
      {displayStages.map((displayStage) => {
        const status = getStageStatus(displayStage);
        const colorClass = statusColors[status] || statusColors.unknown;

        return (
          <div key={displayStage} className="flex flex-col items-center">
            <div
              className={`${dotSize} rounded-full ${colorClass}`}
              title={getTooltip(displayStage)}
            />
            {showLabels && (
              <span className="text-[8px] text-gray-500 mt-0.5">
                {STAGE_SHORT_LABELS[displayStage]}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * ProgressDotsLegend - Shows what each dot color means
 * Useful for first-time users or help sections
 */
export function ProgressDotsLegend({ compact = false }) {
  const items = [
    { status: 'complete', label: 'Complete', color: 'bg-green-500' },
    { status: 'current', label: 'In Progress', color: 'bg-blue-500' },
    { status: 'pending', label: 'Pending', color: 'bg-gray-300' },
  ];

  return (
    <div className={`flex ${compact ? 'gap-3' : 'gap-4'} text-sm text-gray-600`}>
      {items.map(({ status, label, color }) => (
        <div key={status} className="flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${color}`} />
          <span className={compact ? 'text-xs' : ''}>{label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * StageIndicator - Shows current stage as text with optional progress dots
 * Alternative display when dots aren't the best fit
 */
export function StageIndicator({ stage, showDots = true, compact = false }) {
  const label = STAGE_LABELS[stage] || stage;

  const stageColors = {
    PENDING: 'text-gray-500 bg-gray-100',
    PROD_ART: 'text-purple-700 bg-purple-100',
    PRINTING: 'text-blue-700 bg-blue-100',
    ASSEMBLY: 'text-orange-700 bg-orange-100',
    SHIPPING: 'text-green-700 bg-green-100',
  };

  const colorClass = stageColors[stage] || 'text-gray-700 bg-gray-100';

  return (
    <div className="flex items-center gap-2">
      <span
        className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
      >
        {label}
      </span>
      {showDots && <ProgressDots stage={stage} compact={compact} />}
    </div>
  );
}

export default ProgressDots;
